import { useState } from 'react';

import { saveDraftMessage } from '../../../../resources/discussion';
import { DossierId, Participants } from '../../../../resources/dossier/types';
import { useGetDossierData } from '../../../../resources/dossier/queries';
import { useAppDispatch } from '../../../../shared/redux';
import {
    useTrackEventAmplitude,
    useGetTrackedTripProperties,
} from '../../../../shared/analytics/amplitude-hooks';
import { convertSpacesInHtml } from '../utils/messageHtmlConverter';
import {
    fetchAT1Proposal,
    AT1ProposalResponseData,
} from '../components/message-box/fetchAT1Proposal';

type AT1ProposalStatus = 'idle' | 'loading' | 'success' | 'error';

interface UseAT1ProposalParams {
    dossierId: DossierId;
    hasNoMessageInTheDiscussion: boolean;
}

interface UseAT1ProposalResult {
    proposalStatus: AT1ProposalStatus;
    at1ProposalData: AT1ProposalResponseData | null;
    participants: Participants | null | undefined;
    showAT1ProposalBanner: boolean;
    handleGenerateAT1ProposalClick: () => void;
    onAT1ProposalConsumed: () => void;
}

export function useAT1Proposal({
    dossierId,
    hasNoMessageInTheDiscussion,
}: UseAT1ProposalParams): UseAT1ProposalResult {
    const dispatch = useAppDispatch();
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const { participants } = useGetDossierData(dossierId) || {};

    const [proposalStatus, setProposalStatus] = useState<AT1ProposalStatus>('idle');
    const [at1ProposalData, setAt1ProposalData] = useState<AT1ProposalResponseData | null>(null);

    function handleGenerateAT1ProposalClick(): void {
        setProposalStatus('loading');
        trackEvent((ampli, defaultProperties) => {
            ampli.generateAt1MessageClicked({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
        void fetchAT1Proposal(Number(dossierId))
            .then((proposalDataFromFetch) => {
                if (proposalDataFromFetch?.content) {
                    setAt1ProposalData(proposalDataFromFetch);
                    setProposalStatus('success');
                    dispatch(
                        saveDraftMessage(
                            dossierId,
                            proposalDataFromFetch.content,
                            convertSpacesInHtml(proposalDataFromFetch.content),
                        ),
                    );
                }
            })
            .catch(() => {
                setAt1ProposalData(null);
                setProposalStatus('error');
            });
    }

    return {
        proposalStatus,
        at1ProposalData,
        participants,
        showAT1ProposalBanner: !!participants && hasNoMessageInTheDiscussion,
        handleGenerateAT1ProposalClick,
        onAT1ProposalConsumed: () => setAt1ProposalData(null),
    };
}
